import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { SubscriptionService } from '../services/SubscriptionService';
import { SubscriptionRequest, SubscriptionType, RequestStatus } from '../models/SubscriptionRequest';

// Mirrors iOS ProfileView + SubscriptionRequestView
const ProfileView: React.FC = () => {
  const { user, isAdmin, hasValidAccess, hasActiveSubscription, trialDaysRemaining, signOut, refreshUser } = useAuth();
  const [requests, setRequests] = useState<SubscriptionRequest[]>([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber ?? '');
  const [subType, setSubType] = useState<SubscriptionType>(SubscriptionType.MONTHLY);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    SubscriptionService.getUserRequests(user.id)
      .then(setRequests)
      .catch(e => console.error('Failed to load subscription requests:', e));
  }, [user]);

  if (!user) return null;

  const hasPending = requests.some(r => r.status === RequestStatus.PENDING);

  const submitRequest = async () => {
    if (!fullName.trim() || !phoneNumber.trim()) {
      setMessage('Please enter your full name and phone number.');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      await SubscriptionService.submitRequest(user.id, fullName.trim(), phoneNumber.trim(), user.email, subType);
      setRequests(await SubscriptionService.getUserRequests(user.id));
      setShowRequestForm(false);
      setMessage('Subscription request submitted! An admin will review it.');
    } catch (e: any) {
      setMessage(e?.message ?? 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (() => {
    if (isAdmin) return { label: 'Administrator', color: '#9c27b0' };
    if (hasActiveSubscription) return { label: 'Subscribed', color: '#34c759' };
    if (hasValidAccess) return { label: `Trial — ${trialDaysRemaining}d left`, color: '#ff9500' };
    return { label: 'Trial expired', color: '#ff3b30' };
  })();

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', marginBottom: '10px',
    border: '1px solid #ddd', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box'
  };

  return (
    <div style={{ padding: '16px', maxWidth: '560px', margin: '0 auto', overflowY: 'auto', height: '100%' }}>
      {/* Profile card */}
      <div style={{
        backgroundColor: 'white', borderRadius: '16px', padding: '24px',
        border: '1px solid #e8e8e8', textAlign: 'center', marginBottom: '16px'
      }}>
        <div style={{
          width: '72px', height: '72px', borderRadius: '50%', margin: '0 auto 12px',
          backgroundColor: '#007aff', color: 'white', fontSize: '30px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700
        }}>
          {user.displayName.charAt(0).toUpperCase()}
        </div>
        <h2 style={{ margin: '0 0 4px' }}>{user.displayName}</h2>
        {user.fullName && <p style={{ margin: '0 0 2px', color: '#666', fontSize: '14px' }}>{user.fullName}</p>}
        <p style={{ margin: '0 0 12px', color: '#888', fontSize: '13px' }}>{user.email}</p>
        <span style={{
          display: 'inline-block', padding: '4px 14px', borderRadius: '14px',
          backgroundColor: `${statusBadge.color}18`, color: statusBadge.color,
          fontSize: '13px', fontWeight: 700
        }}>
          {statusBadge.label}
        </span>
      </div>

      {/* Subscription section */}
      {!isAdmin && (
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '20px',
          border: '1px solid #e8e8e8', marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '17px' }}>Subscription</h3>
          {hasActiveSubscription ? (
            <p style={{ color: '#34c759', fontSize: '14px', margin: 0 }}>
              Active {user.subscriptionType ?? ''} subscription
              {user.subscriptionEndDate && ` until ${user.subscriptionEndDate.toLocaleDateString()}`}
            </p>
          ) : hasPending ? (
            <p style={{ color: '#ff9500', fontSize: '14px', margin: 0 }}>
              Your subscription request is pending review.
            </p>
          ) : (
            <>
              <p style={{ color: '#666', fontSize: '14px', margin: '0 0 12px' }}>
                {hasValidAccess
                  ? `Your free trial ends in ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'}.`
                  : 'Your trial has expired. Subscribe to keep full access.'}
              </p>
              {!showRequestForm ? (
                <button
                  onClick={() => setShowRequestForm(true)}
                  style={{
                    padding: '10px 18px', borderRadius: '10px', border: 'none',
                    backgroundColor: '#007aff', color: 'white', fontWeight: 600,
                    cursor: 'pointer', fontSize: '14px'
                  }}
                >
                  Request Subscription
                </button>
              ) : (
                <div>
                  <input style={inputStyle} placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} />
                  <input style={inputStyle} placeholder="Phone number" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} />
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    {Object.values(SubscriptionType).map(t => (
                      <button
                        key={t}
                        onClick={() => setSubType(t)}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '10px', cursor: 'pointer',
                          border: `2px solid ${subType === t ? '#007aff' : '#e0e0e0'}`,
                          backgroundColor: subType === t ? '#007aff15' : 'white',
                          fontWeight: subType === t ? 700 : 500, fontSize: '14px'
                        }}
                      >
                        {t === SubscriptionType.MONTHLY ? 'Monthly' : 'Yearly'}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setShowRequestForm(false)} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #ddd', backgroundColor: 'white', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button
                      onClick={submitRequest}
                      disabled={submitting}
                      style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', backgroundColor: '#007aff', color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {submitting ? 'Submitting…' : 'Submit'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {message && <p style={{ marginTop: '10px', fontSize: '13px', color: '#555' }}>{message}</p>}
        </div>
      )}

      {/* Request history */}
      {requests.length > 0 && (
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '20px',
          border: '1px solid #e8e8e8', marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 10px', fontSize: '17px' }}>Request History</h3>
          {requests.map(r => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
              <span>{r.subscriptionType} • {r.requestedAt.toLocaleDateString()}</span>
              <span style={{
                fontWeight: 600,
                color: r.status === RequestStatus.APPROVED ? '#34c759' : r.status === RequestStatus.REJECTED ? '#ff3b30' : '#ff9500'
              }}>
                {r.status}
              </span>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={async () => { await signOut(); }}
        style={{
          width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid #ff3b3033',
          backgroundColor: '#ff3b3010', color: '#ff3b30', fontWeight: 700,
          cursor: 'pointer', fontSize: '15px'
        }}
      >
        Sign Out
      </button>
      <button onClick={refreshUser} style={{ display: 'none' }} />
    </div>
  );
};

export default ProfileView;
